import React, { forwardRef } from 'react';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'destructive';
  tooltip?: string;
}

/**
 * IconButton component following BuilderBel Brand Guidelines
 *
 * Brand Guidelines Implementation:
 * - One icon set (uniform stroke); sizes 16/20/24px
 * - Hover: subtle bg/border change
 * - Focus: visible 2px ring
 * - Default: transparent background with hover
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, size = 'md', variant = 'default', tooltip, className = '', ...props }, ref) => {
    const baseClasses = 'icon-button';

    const sizeClasses = {
      sm: 'p-1',
      md: 'p-2',
      lg: 'p-3',
    };

    const variantClasses = {
      default: '',
      primary: 'text-brand-600 hover:bg-brand-50 hover:text-brand-700',
      destructive: 'text-red-600 hover:bg-red-50 hover:text-red-700',
    };

    const iconSizeClasses = {
      sm: 'icon-sm',
      md: 'icon-md',
      lg: 'icon-lg',
    };

    const classes = [baseClasses, sizeClasses[size], variantClasses[variant], className]
      .filter(Boolean)
      .join(' ');

    const iconClass = iconSizeClasses[size];

    // Clone children and add icon size class
    const iconElement = React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement<{ className?: string }>, {
          className:
            `${iconClass} ${(children as React.ReactElement<{ className?: string }>).props?.className || ''}`.trim(),
        })
      : children;

    return (
      <button ref={ref} title={tooltip} className={classes} {...props}>
        {iconElement}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
